/**
 * Znaki typów relacji — **własny zestaw SVG**, bo są językiem produktu (spec 12B.7).
 *
 * ⭐ Decyzja 58: *„relacje = kształt, stany = kolor"*. Kształt jest jedynym nośnikiem typu
 * relacji, więc musi wyglądać identycznie u każdego — a znak czcionki (`■ □ ↓ ↑`) tego nie
 * daje: rysuje go czcionka systemowa odbiorcy, `⚠` bywa kolorowym emoji ignorującym `color`,
 * a przy nadpisanej czcionce (czytelność, dysleksja) w miejscu znaku zostaje pusty prostokąt.
 * ⚠ To NIE jest zamiana kosmetyczna: znak, którego kształtu nie kontrolujemy, nie może być
 * jedynym nośnikiem znaczenia.
 *
 * ── Dlaczego własne SVG, a nie lucide ────────────────────────────────────────
 * Cztery znaki są **jedną rodziną** i muszą trzymać wspólną wagę optyczną: wypełniony
 * kwadrat kontra obrys to cała różnica między „zawiera" a „część czegoś". Wzięcie dwóch
 * strzałek z biblioteki, a dwóch kwadratów z własnej ręki rozjechałoby rodzinę przy
 * pierwszej aktualizacji lucide.
 *
 * ── Geometria (przewodnik lucide, żeby siadły obok jego ikon jak swoje) ──────
 * Płótno **24×24** · obrys **2** · zakończenia i złącza **zaokrąglone** · obrys **wyśrodkowany**
 * · min. **1** marginesu · promień **2** dla kształtów ≥8. `currentColor`, więc barwę bierze
 * z tokenu warstwy semantycznej — czego glif emoji nie umiał.
 *
 * ⚠ Kwadrat „zawiera" jest WĘŻSZY od obrysowego (16 zamiast 18): wypełnienie waży optycznie
 * więcej niż kreska, więc przy równych bokach wyglądałby na większy, a nie na inny.
 */

/** Cztery kierunki z zestawu A (decyzja 54). ⚠ Własny typ, bo `shared/` nie zna `features/`. */
export type RelationShape = 'has' | 'part-of' | 'depends-on' | 'used-by';

interface RelationIconProps {
  direction: RelationShape;
  /** Domyślnie 14 — wiersz drzewka i sekcje relacji są gęste. */
  size?: number;
}

export function RelationIcon({ direction, size = 14 }: RelationIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {SHAPES[direction]}
    </svg>
  );
}

const SHAPES: Record<RelationShape, React.ReactElement> = {
  // „zawiera" — pełny kwadrat: rodzic niesie dzieci, więc znak jest ciężki.
  has: <rect x="4" y="4" width="16" height="16" rx="2" fill="currentColor" stroke="none" />,

  // „część czegoś" — ten sam kwadrat, ale pusty: odwrotność `has`, więc odwrotność wypełnienia.
  'part-of': <rect x="3" y="3" width="18" height="18" rx="2" />,

  // „zależy od" — strzałka W DÓŁ: patrzymy na to, co pode mną i co mnie zatrzyma.
  'depends-on': (
    <>
      <path d="M12 4v14" />
      <path d="m5 13 7 7 7-7" />
    </>
  ),

  // „używane przez" — ta sama strzałka odbita: zasięg zmiany idzie w GÓRĘ.
  'used-by': (
    <>
      <path d="M12 20V6" />
      <path d="m5 11 7-7 7 7" />
    </>
  ),
};
